/**
 * Sprint TF-1 Phase 1 — Admin TalentFix (HM Advisor / HM Admin Case Board).
 *
 * Stage-1 intake desk. Admins / advisors can:
 *   - Browse cases scoped to their book (advisor) or the whole platform (admin)
 *   - Filter by status / origin
 *   - Open a case detail view with classify · accept · decline actions
 *   - View the case timeline
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Wrench, Loader2, Inbox, Calendar, Building2, User, CheckCircle2, XCircle, Settings2,
  Clock, ArrowRight, FileEdit, ShieldCheck,
} from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import OpenCaseDialog from '@/components/talentfix/OpenCaseDialog';
import { CaseStatusBadge, CaseScopeBadge, CaseOriginPill } from '@/components/talentfix/CaseBadges';
import { getUiLocale } from '@/lib/dates';

const SEVERITIES = ['informational', 'moderate', 'severe', 'critical'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const SCOPES_C   = ['standard', 'restricted', 'privileged', 'legal_hold'];

function CaseRow({ c, onSelect, selected }) {
  const { t } = useTranslation();
  const created = c.created_at ? new Date(c.created_at).toLocaleDateString(getUiLocale()) : '—';
  return (
    <button
      type="button"
      onClick={() => onSelect(c)}
      data-testid={`tf-admin-row-${c.id}`}
      className={`w-full text-left p-4 rounded-lg border bg-card transition-smooth ${
        selected ? 'border-primary ring-1 ring-primary/30' : 'border-border/70 hover:border-primary/40 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <CaseOriginPill origin={c.origin_type} />
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{t(`talentFix.scope.${c.scope_unit}`)}</span>
          </div>
          <p className="font-semibold truncate">{c.title || '—'}</p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{c.company_name || c.company_id?.slice(0, 8)}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{created}</span>
            {c.requested_by_name && (
              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{c.requested_by_name}</span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <CaseStatusBadge status={c.status} />
          <CaseScopeBadge scope={c.confidentiality_scope} />
        </div>
      </div>
    </button>
  );
}

function ClassifyDialog({ open, onOpenChange, caseObj, onSubmit, canSetLegalHold }) {
  const { t } = useTranslation();
  const isPreventive = caseObj?.origin_type === 'preventive_advisory';
  const [severity, setSeverity] = useState(caseObj?.severity || 'moderate');
  const [priority, setPriority] = useState(caseObj?.strategic_priority || 'medium');
  const [confidentiality, setConfidentiality] = useState(caseObj?.confidentiality_scope || 'standard');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (caseObj) {
      setSeverity(caseObj.severity || 'moderate');
      setPriority(caseObj.strategic_priority || 'medium');
      setConfidentiality(caseObj.confidentiality_scope || 'standard');
      setNote('');
    }
  }, [caseObj]);

  const submit = useCallback(async () => {
    if (!caseObj) return;
    setBusy(true);
    try {
      const body = { confidentiality_scope: confidentiality };
      if (isPreventive) body.strategic_priority = priority;
      else body.severity = severity;
      if (note.trim()) body.note = note.trim();
      await onSubmit(caseObj.id, body);
      toast.success(t('talentFix.successClassify'));
      onOpenChange(false);
    } catch (e) {
      toast.error(t('talentFix.errClassify'), { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  }, [caseObj, severity, priority, confidentiality, note, isPreventive, onSubmit, onOpenChange, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf-classify-dialog">
        <DialogHeader>
          <DialogTitle>{t('talentFix.classify')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isPreventive ? (
            <div>
              <Label className="text-xs uppercase tracking-wider">{t('talentFix.priority')}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="tf-classify-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((s) => <SelectItem key={s} value={s}>{t(`talentFix.priorityValue.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-wider">{t('talentFix.severityOrPriority')}</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="tf-classify-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{t(`talentFix.severity.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider">{t('talentFix.confidentiality')}</Label>
            <Select value={confidentiality} onValueChange={setConfidentiality}>
              <SelectTrigger data-testid="tf-classify-conf"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES_C
                  .filter((s) => s !== 'legal_hold' || canSetLegalHold)
                  .map((s) => <SelectItem key={s} value={s}>{t(`talentFix.scopeC.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{t(`talentFix.scopeCHint.${confidentiality}`)}</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">{t('talentFix.classifyNote')}</Label>
            <Textarea data-testid="tf-classify-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>{t('talentFix.cancel')}</Button>
          <Button onClick={submit} disabled={busy} data-testid="tf-classify-submit" className="gap-1.5">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('talentFix.classify')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ open, onOpenChange, caseObj, onSubmit }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [redirect, setRedirect] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setReason(''); setRedirect(''); } }, [open]);

  const submit = useCallback(async () => {
    if (reason.trim().length < 4) return;
    setBusy(true);
    try {
      await onSubmit(caseObj.id, { reason: reason.trim(), redirect_to: redirect.trim() || undefined });
      toast.success(t('talentFix.successDecline'));
      onOpenChange(false);
    } catch (e) {
      toast.error(t('talentFix.errDecline'), { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  }, [caseObj, reason, redirect, onSubmit, onOpenChange, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf-decline-dialog">
        <DialogHeader>
          <DialogTitle>{t('talentFix.decline')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">{t('talentFix.declineReason')}</Label>
            <Textarea data-testid="tf-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('talentFix.declineReasonPlaceholder')} rows={3} maxLength={2000} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">{t('talentFix.declineRedirectTo')}</Label>
            <Input data-testid="tf-decline-redirect" value={redirect} onChange={(e) => setRedirect(e.target.value)} placeholder={t('talentFix.declineRedirectPlaceholder')} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>{t('talentFix.cancel')}</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 4} data-testid="tf-decline-submit" variant="destructive" className="gap-1.5">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('talentFix.decline')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CaseDetail({ caseObj, onAccept, onDeclineRequest, onClassifyRequest, timeline, diagnosticPlan }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  if (!caseObj) {
    return (
      <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="tf-admin-no-selection">
        <FileSearchIcon />
        {isES ? 'Selecciona un caso de la lista para ver los detalles.' : 'Select a case from the list to see details.'}
      </Card>
    );
  }
  const isPending = caseObj.status === 'pending';
  const isAccepted = caseObj.status === 'accepted' || caseObj.status === 'in_progress';
  const requested = caseObj.created_at ? new Date(caseObj.created_at).toLocaleString(getUiLocale()) : '—';
  const isPreventive = caseObj.origin_type === 'preventive_advisory';
  const planStatus = diagnosticPlan?.status || null;
  const planExists = !!diagnosticPlan;

  return (
    <Card className="p-5 shadow-card" data-testid={`tf-admin-detail-${caseObj.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CaseOriginPill origin={caseObj.origin_type} />
          <h3 className="text-lg font-semibold mt-1" data-testid="tf-admin-detail-title">{caseObj.title}</h3>
          <p className="text-xs text-muted-foreground mt-1">{t(`talentFix.scope.${caseObj.scope_unit}`)} · {caseObj.company_name}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <CaseStatusBadge status={caseObj.status} />
          <CaseScopeBadge scope={caseObj.confidentiality_scope} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldCreated')}</p>
          <p className="font-medium">{requested}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldRequester')}</p>
          <p className="font-medium">{caseObj.requested_by_name || '—'}</p>
        </div>
        <div data-testid="tf-admin-detail-advisor">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{isES ? 'Consultor CTH asignado' : 'Assigned HM Advisor'}</p>
          <p className="font-medium inline-flex items-center gap-1">
            {caseObj.assigned_advisor_name
              ? <><ShieldCheck className="h-3.5 w-3.5 text-primary" />{caseObj.assigned_advisor_name}</>
              : <span className="italic text-muted-foreground">{isES ? '— sin asignar —' : '— not yet assigned —'}</span>}
          </p>
        </div>
        <div data-testid="tf-admin-detail-plan-status">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{isES ? 'Plan de diagnóstico' : 'Diagnostic Plan'}</p>
          <p className="font-medium">
            {planExists
              ? <span className="capitalize">{(planStatus || '').replace(/_/g, ' ')}</span>
              : (isAccepted ? <span className="italic text-muted-foreground">{isES ? 'No iniciado' : 'Not started'}</span>
                            : <span className="italic text-muted-foreground">—</span>)}
          </p>
        </div>
        {!isPreventive && caseObj.severity && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.severityOrPriority')}</p>
            <p className="font-medium">{t(`talentFix.severity.${caseObj.severity}`)}</p>
          </div>
        )}
        {isPreventive && caseObj.strategic_priority && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.priority')}</p>
            <p className="font-medium">{t(`talentFix.priorityValue.${caseObj.strategic_priority}`)}</p>
          </div>
        )}
        {isPreventive && caseObj.target_horizon_months && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.fieldHorizon')}</p>
            <p className="font-medium">{t('talentFix.monthsShort', { n: caseObj.target_horizon_months })}</p>
          </div>
        )}
      </div>

      {caseObj.description && (
        <div className="mt-4 p-3 rounded-md bg-muted/40 text-sm whitespace-pre-wrap" data-testid="tf-admin-detail-description">
          {caseObj.description}
        </div>
      )}

      {caseObj.status === 'declined' && caseObj.decline_reason && (
        <div className="mt-3 p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm" data-testid="tf-admin-detail-decline-reason">
          <p className="text-xs uppercase tracking-wider text-destructive font-medium">{t('talentFix.declineReason')}</p>
          <p className="mt-1">{caseObj.decline_reason}</p>
          {caseObj.decline_redirect_to && <p className="text-xs text-muted-foreground mt-1">→ {caseObj.decline_redirect_to}</p>}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => onClassifyRequest(caseObj)} disabled={!isPending} data-testid="tf-action-classify" className="gap-1.5">
          <Settings2 className="h-4 w-4" /> {t('talentFix.classify')}
        </Button>
        <Button size="sm" onClick={() => onAccept(caseObj)} disabled={!isPending} data-testid="tf-action-accept" className="gap-1.5">
          <CheckCircle2 className="h-4 w-4" /> {t('talentFix.accept')}
        </Button>
        <Button size="sm" variant="destructive" onClick={() => onDeclineRequest(caseObj)} disabled={!isPending} data-testid="tf-action-decline" className="gap-1.5">
          <XCircle className="h-4 w-4" /> {t('talentFix.decline')}
        </Button>
        {isAccepted && (
          <Link to={`/admin/talentfix/cases/${caseObj.id}/diagnostic-plan`}>
            <Button size="sm" data-testid="tf-action-diagnostic-plan" className="gap-1.5">
              <FileEdit className="h-4 w-4" />
              {planExists ? 'Open Diagnostic Plan' : 'Create Diagnostic Plan'}
            </Button>
          </Link>
        )}
        {isAccepted && planStatus === 'consent_granted' && (
          <Link to={`/admin/talentfix/cases/${caseObj.id}/investigation`}>
            <Button size="sm" variant="secondary" data-testid="tf-action-investigation" className="gap-1.5">
              <ShieldCheck className="h-4 w-4" />Open Investigation
            </Button>
          </Link>
        )}
        {/* TF-A — Closure pack quick actions (HM Admin / Advisor in-book). */}
        {caseObj.status === 'closed' && (
          <>
            <Button size="sm" variant="outline" data-testid="tf-action-closure-pdf" className="gap-1.5"
                    onClick={() => adminApi.tfRenderClosure(caseObj.id, 'pdf')}>
              <FileEdit className="h-4 w-4" /> Closure PDF
            </Button>
            <Button size="sm" variant="outline" data-testid="tf-action-closure-docx" className="gap-1.5"
                    onClick={() => adminApi.tfRenderClosure(caseObj.id, 'docx')}>
              DOCX
            </Button>
            <Button size="sm" variant="outline" data-testid="tf-action-closure-xlsx" className="gap-1.5"
                    onClick={() => adminApi.tfRenderClosure(caseObj.id, 'xlsx')}>
              XLSX
            </Button>
          </>
        )}
      </div>

      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> {t('talentFix.timeline')}
        </p>
        <ul className="space-y-1.5" data-testid="tf-admin-detail-timeline">
          {(timeline || []).length === 0 ? (
            <li className="text-xs text-muted-foreground">—</li>
          ) : timeline.map((e) => (
            <li key={e.id} className="text-xs flex items-start gap-2" data-testid={`tf-tl-${e.kind}`}>
              <ArrowRight className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-medium">{e.kind}</span>
                <span className="text-muted-foreground"> · {e.by_user_name || e.by_user_role || '—'} · {new Date(e.at).toLocaleString(getUiLocale())}</span>
                {e.note && <p className="text-muted-foreground italic">"{e.note}"</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

function FileSearchIcon() {
  return <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />;
}

export default function AdminTalentFix() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [diagnosticPlan, setDiagnosticPlan] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrigin, setFilterOrigin] = useState('all');

  const [openDialog, setOpenDialog] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterOrigin !== 'all') params.origin_type = filterOrigin;
      const data = await adminApi.talentFixListCases(params);
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterOrigin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminApi.talentFixListCases({});
        // also fetch current user so we know if we can set legal_hold
      } catch (_e) { /* ignored */ }
      try {
        const r = await import('@/lib/api');
        const res = await r.default.get('/auth/me');
        setMe(res.data);
      } catch (_e) { /* ignored */ }
    })();
  }, []);

  const loadTimeline = useCallback(async (cid) => {
    try {
      const data = await adminApi.talentFixGetTimeline(cid);
      setTimeline(data?.items || []);
    } catch (_e) { setTimeline([]); }
  }, []);

  const loadDiagnosticPlan = useCallback(async (cid) => {
    try {
      const data = await adminApi.talentFixGetDiagnosticPlan(cid);
      setDiagnosticPlan(data?.plan || null);
    } catch (_e) { setDiagnosticPlan(null); }
  }, []);

  const onSelect = useCallback((c) => {
    setSelected(c);
    setDiagnosticPlan(null);
    loadTimeline(c.id);
    loadDiagnosticPlan(c.id);
  }, [loadTimeline, loadDiagnosticPlan]);

  const refreshCase = useCallback(async (cid) => {
    const fresh = await adminApi.talentFixGetCase(cid);
    setSelected(fresh);
    await load();
    await loadTimeline(cid);
    await loadDiagnosticPlan(cid);
  }, [load, loadTimeline, loadDiagnosticPlan]);

  const handleAccept = useCallback(async (c) => {
    try {
      await adminApi.talentFixAccept(c.id, {});
      toast.success(t('talentFix.successAccept'));
      await refreshCase(c.id);
    } catch (e) {
      toast.error(t('talentFix.errAccept'), { description: formatApiError(e) });
    }
  }, [refreshCase, t]);

  const handleClassifySubmit = useCallback(async (cid, body) => {
    await adminApi.talentFixClassify(cid, body);
    await refreshCase(cid);
  }, [refreshCase]);

  const handleDeclineSubmit = useCallback(async (cid, body) => {
    await adminApi.talentFixDecline(cid, body);
    await refreshCase(cid);
  }, [refreshCase]);

  const handleOpenAdminSubmit = useCallback(async (body) => {
    if (!body.company_id) {
      // Admin path requires company_id; pick first known company if not chosen.
      // For TF-1 simplicity, just surface the error.
      throw new Error('company_id_required');
    }
    const created = await adminApi.talentFixOpenCase(body);
    setSelected(created);
    await load();
    await loadTimeline(created.id);
  }, [load, loadTimeline]);

  const summary = useMemo(() => ({
    pending: items.filter((c) => c.status === 'pending').length,
    accepted: items.filter((c) => c.status === 'accepted' || c.status === 'in_progress').length,
    closed: items.filter((c) => c.status === 'closed' || c.status === 'declined').length,
  }), [items]);

  const canSetLegalHold = me?.role === 'hm_admin';

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="admin-talentfix-page">
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            {t('talentFix.title')}
          </span>
        )}
        subtitle={t('talentFix.subtitleAdmin')}
        actions={(
          <Link to="/admin/talentfix/oversight" data-testid="tf-action-oversight-link">
            <Button size="sm" variant="outline" className="gap-1.5">
              <ShieldCheck className="h-4 w-4" />
              {t('talentFix.oversight', { defaultValue: 'Oversight' })}
            </Button>
          </Link>
        )}
      />

      {/* KPI strip — clickable, drills into the case tabs below */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tf-admin-summary">
        <Card className="p-4 cursor-pointer transition-colors hover:border-primary/40" role="button"
              onClick={() => setFilterStatus('pending')} data-testid="tf-kpi-pending">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.filterPending')}</p>
          <p className="text-2xl font-bold tabular-nums">{summary.pending}</p>
        </Card>
        <Card className="p-4 cursor-pointer transition-colors hover:border-primary/40" role="button"
              onClick={() => setFilterStatus('accepted')} data-testid="tf-kpi-accepted">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.filterAccepted')}</p>
          <p className="text-2xl font-bold tabular-nums">{summary.accepted}</p>
        </Card>
        <Card className="p-4 cursor-pointer transition-colors hover:border-primary/40" role="button"
              onClick={() => setFilterStatus('closed')} data-testid="tf-kpi-closed">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('talentFix.filterClosed')}</p>
          <p className="text-2xl font-bold tabular-nums">{summary.closed}</p>
        </Card>
      </div>

      {/* Founder UAT — case supervision layer (role-aware; aggregate only, does
          not expose privileged case content beyond the existing list fields) */}
      <SuiteOpsPanel suiteCode="talentfix" onKpiAction={(target) => setFilterStatus(target)} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 shadow-card">
          <Tabs value={filterStatus} onValueChange={setFilterStatus} data-testid="tf-admin-filter-tabs">
            <TabsList>
              <TabsTrigger value="all">{t('talentFix.filterAll')} ({items.length})</TabsTrigger>
              <TabsTrigger value="pending">{t('talentFix.filterPending')}</TabsTrigger>
              <TabsTrigger value="accepted">{t('talentFix.filterAccepted')}</TabsTrigger>
              <TabsTrigger value="closed">{t('talentFix.filterClosed')}</TabsTrigger>
            </TabsList>
            <TabsContent value={filterStatus} className="mt-3">
              {loading ? (
                <div className="py-10 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="tf-admin-empty">
                  <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  {t('talentFix.emptyAdmin')}
                </div>
              ) : (
                <ul className="space-y-2" data-testid="tf-admin-list">
                  {items.map((c) => (
                    <li key={c.id}>
                      <CaseRow c={c} selected={selected?.id === c.id} onSelect={onSelect} />
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </Tabs>
        </Card>

        <div>
          <CaseDetail
            caseObj={selected}
            onAccept={handleAccept}
            onClassifyRequest={(c) => { setSelected(c); setClassifyOpen(true); }}
            onDeclineRequest={(c) => { setSelected(c); setDeclineOpen(true); }}
            timeline={timeline}
            diagnosticPlan={diagnosticPlan}
          />
        </div>
      </div>

      <ClassifyDialog
        open={classifyOpen}
        onOpenChange={setClassifyOpen}
        caseObj={selected}
        onSubmit={handleClassifySubmit}
        canSetLegalHold={canSetLegalHold}
      />
      <DeclineDialog
        open={declineOpen}
        onOpenChange={setDeclineOpen}
        caseObj={selected}
        onSubmit={handleDeclineSubmit}
      />
      <OpenCaseDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onSubmit={handleOpenAdminSubmit}
        isAdmin
      />
    </div>
  );
}
